import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight, Activity, Box, Cloud, Cpu, Database, Sparkles, Workflow, GitMerge, LineChart, ListChecks, Mail, Target, Crosshair, Send, Search, Snowflake, BarChart3, PieChart, Compass, Tag } from "lucide-react";
import Seo from "@/components/site/Seo";
import JsonLd from "@/components/site/JsonLd";
import BrandLogos from "@/components/site/BrandLogos";
import BrandExperience from "@/components/site/BrandExperience";
import DataFlowGraphic from "@/components/site/DataFlowGraphic";
import BookCall from "@/components/site/BookCall";
import Reveal from "@/components/site/Reveal";
import { trackEvent } from "@/lib/tracking";

// Problem-led service entry points. Each card opens with the operational pain,
// not the service name.
const PROBLEM_CARDS = [
  {
    icon: Workflow,
    to: "/marketo-services",
    problem: "Adobe Marketo Engage is doing more work than it's set up for.",
    fix: "Instance audit, program structure, scoring, and lifecycle work — done by senior Marketo operators, not a junior pool.",
    pill: "Marketo Services",
  },
  {
    icon: GitMerge,
    to: "/services/salesforce-crm",
    problem: "Marketo and Salesforce don't agree on lead status.",
    fix: "Sync hardening, field mapping, lifecycle stages, lead routing, and SLA tracking that marketing and sales can both trust.",
    pill: "Marketo ↔ Salesforce",
  },
  {
    icon: Sparkles,
    to: "/services/campaign-operations",
    problem: "Campaigns are taking too long to launch.",
    fix: "Intake → build → QA → launch as one repeatable process. Templates, governance, and QA checks so launches stop slipping.",
    pill: "Campaign Operations",
  },
  {
    icon: ListChecks,
    to: "/services/managed-services",
    problem: "The MOPs team is buried and execution is slipping.",
    fix: "Senior, embedded marketing operations capacity for week-to-week execution, QA, troubleshooting, and platform upkeep.",
    pill: "Managed Operations",
  },
  {
    icon: Database,
    to: "/services/martech-integration",
    problem: "The stack is connected but nothing is really aligned.",
    fix: "Field standardization, UTM and source taxonomy, consent capture, dedupe, and platform alignment so the data downstream is usable.",
    pill: "Martech & Data",
  },
  {
    icon: LineChart,
    to: "/services/reporting-attribution",
    problem: "Reports don't tie back to pipeline.",
    fix: "Source field architecture, GA4 events, program-to-campaign reporting, and dashboards an executive team will actually open.",
    pill: "Reporting & Attribution",
  },
];

// Top tools in a modern B2B martech stack — curated around Adobe Marketo Engage + Salesforce
const TRUST_BADGES = [
  { icon: Workflow, label: "Adobe Marketo Engage" },
  { icon: Cloud, label: "Salesforce" },
  { icon: Sparkles, label: "Adobe Experience Cloud" },
  { icon: Activity, label: "Adobe Analytics" },
  { icon: Box, label: "HubSpot" },
  { icon: Target, label: "6sense" },
  { icon: Crosshair, label: "Demandbase" },
  { icon: Send, label: "Outreach" },
  { icon: Mail, label: "Salesloft" },
  { icon: Search, label: "ZoomInfo" },
  { icon: GitMerge, label: "Segment" },
  { icon: Snowflake, label: "Snowflake" },
  { icon: BarChart3, label: "GA4" },
  { icon: LineChart, label: "Tableau" },
  { icon: PieChart, label: "Looker" },
  { icon: Compass, label: "Bizible" },
  { icon: Tag, label: "Tealium" },
];

const PAINS = [
  { p: "Marketo and Salesforce don't agree on lead status", d: "Sync errors, picklist drift, and field mismatches quietly break reporting." },
  { p: "Lifecycle stages are unclear or inconsistent", d: "Definitions live in three heads. Conversion rates lose meaning." },
  { p: "Lead routing is unreliable", d: "MQLs land on the wrong owner, or wait too long for a touch." },
  { p: "Campaigns take too long to launch", d: "Intake is informal, QA is whoever has time, launches slip on Friday." },
  { p: "QA is inconsistent across builds", d: "Each marketer QAs differently. The defects show up in production." },
  { p: "Reporting doesn't reflect campaign reality", d: "UTM hygiene is loose, source fields are noisy, dashboards disagree." },
  { p: "MOPs team is stretched thin", d: "Strategy work gets pushed because tickets and ad-hoc requests win the day." },
  { p: "Senior hands-on support is missing", d: "Vendors hand-off to juniors. The team needs an operator, not a slide deck." },
];

const PROCESS = [
  { n: "01", t: "Audit", b: "We open your Marketo and Salesforce setup, review the campaign and lifecycle process, and surface where pipeline is leaking. Written assessment, not a slideshow." },
  { n: "02", t: "Design", b: "We define lifecycle stages, routing logic, sync rules, and the campaign templates the team will actually use — with sign-off from marketing and sales." },
  { n: "03", t: "Build", b: "Programs, smart campaigns, forms, landing pages, scoring, and Salesforce flows — built, named, and documented so they can be inherited." },
  { n: "04", t: "Launch", b: "QA, shadow mode where it makes sense, and a clean cutover. We sweat the boring details so launches stop slipping." },
  { n: "05", t: "Run", b: "Ongoing execution, QA, troubleshooting, and quarterly health checks. Senior MOPs capacity that doesn't disappear after launch." },
];

export default function Home() {
  return (
    <>
      <Seo
        title="Xadentra | B2B Marketing Automation & Marketo Services"
        description="Xadentra is a specialist B2B marketing automation agency. We fix broken Marketo/Salesforce sync, lifecycle, lead routing, campaign QA, and reporting for revenue teams."
        path="/"
      />
      <JsonLd
        id="home-org"
        data={{
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "Xadentra",
          url: "https://xadentra.com/",
          logo: "https://xadentra.com/favicon.svg",
          description:
            "Specialist B2B marketing automation agency. Adobe Marketo Engage, Salesforce CRM, campaign operations, lifecycle, lead routing, and reporting for enterprise and SaaS revenue teams.",
          sameAs: ["https://www.linkedin.com/company/xadentra/"],
          contactPoint: [
            {
              "@type": "ContactPoint",
              contactType: "Sales",
              email: "contact" + "@" + "xadentra.com",
              availableLanguage: ["English"],
              areaServed: "Worldwide",
            },
          ],
        }}
      />
      <JsonLd
        id="home-website"
        data={{
          "@context": "https://schema.org",
          "@type": "WebSite",
          name: "Xadentra",
          url: "https://xadentra.com/",
        }}
      />

      {/* HERO — problem-led */}
      <section data-testid="hero-section" className="relative bg-[#07111F] text-white overflow-hidden">
        <div className="xa-blob" style={{ width: 520, height: 520, background: "#6D4DFF", top: -180, left: -120 }} />
        <div className="xa-blob" style={{ width: 480, height: 480, background: "#00D4FF", bottom: -200, right: -120, opacity: 0.4 }} />
        <div className="absolute inset-0 xa-grid-dark opacity-60 pointer-events-none" />

        <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-20 lg:pt-28 pb-24 relative grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7 xa-fade-up">
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.02]">
              Fix the <span className="xa-gradient-text">marketing operations</span> that&apos;s quietly slowing pipeline.
            </h1>

            <p className="mt-7 text-lg lg:text-xl text-white/70 leading-relaxed max-w-2xl">
              Xadentra is a specialist B2B marketing automation agency.
              We work directly inside your <Link to="/marketo-services" className="text-white hover:text-[#00D4FF] underline decoration-white/30 hover:decoration-[#00D4FF] underline-offset-4 mx-1">Adobe Marketo Engage</Link>
              and Salesforce setup — fixing broken sync, messy lifecycle stages, unreliable lead routing,
              slow campaign launches, and reporting that no one trusts.
            </p>

            <div className="mt-9 flex flex-col sm:flex-row gap-3">
              <BookCall source="hero_primary" />
              <Link
                to="/services"
                data-testid="hero-cta-secondary"
                onClick={() => trackEvent("cta_click", { id: "hero_explore_services" })}
                className="xa-btn xa-btn-ghost-dark"
              >
                See what we fix <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            <ul className="mt-12 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm text-white/80 max-w-2xl">
              {[
                "Broken Marketo / Salesforce sync, hardened",
                "Lifecycle stages and routing the team can rely on",
                "Campaign launches with a real QA process",
                "Reporting that ties activity back to pipeline",
              ].map((p, i) => (
                <li key={i} className="flex items-start gap-2.5">
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#00D4FF] shrink-0" />
                  <span>{p}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-5 xa-fade-up xa-delay-2 relative">
            <div className="xa-card-dark rounded-2xl p-4 lg:p-6">
              <DataFlowGraphic className="w-full h-auto" />
            </div>
            <div className="mt-6 grid grid-cols-2 gap-3">
              <div className="xa-card-dark rounded-xl p-5">
                <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#00D4FF]">Senior operators</div>
                <div className="font-display text-2xl font-bold mt-2">Marketo<br />+ Salesforce</div>
              </div>
              <div className="xa-card-dark rounded-xl p-5">
                <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#3EE8A5]">Hands-on</div>
                <div className="font-display text-2xl font-bold mt-2">Build, QA,<br />and run</div>
              </div>
            </div>
          </div>
        </div>

        {/* Trust ribbon */}
        <div className="border-t border-white/10 bg-white/[0.02]">
          <div className="max-w-7xl mx-auto px-6 lg:px-8 py-6 flex items-center gap-6">
            <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-white/40 whitespace-nowrap">
              Built around
            </span>
            <div className="flex-1 overflow-hidden">
              <div className="xa-marquee-track gap-16 pl-10">
                {[...TRUST_BADGES, ...TRUST_BADGES].map((L, i) => (
                  <div key={i} className="flex items-center gap-2.5 text-white/70 hover:text-white transition-colors">
                    <L.icon className="w-4 h-4 text-[#00D4FF]" />
                    <span className="font-mono text-xs tracking-wider">{L.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURED PLATFORMS — Adobe Marketo Engage + Salesforce */}
      <BrandLogos variant="dark" />

      {/* BRAND EXPERIENCE — team experience with leading global brands */}
      <BrandExperience />

      {/* PROBLEMS WE FIX — replaces generic "what we do" */}
      <section className="bg-[#F7F9FC] border-b border-[#E5E7EB]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
            <div className="lg:col-span-7">
              <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#6D4DFF]">What we fix</div>
              <h2 className="font-display mt-4 text-4xl lg:text-5xl font-bold tracking-tight text-[#07111F]">
                Most pipeline gaps aren&apos;t a creative problem. They&apos;re an operations problem.
              </h2>
            </div>
            <div className="lg:col-span-5">
              <p className="text-[#6B7280] leading-relaxed">
                These are the patterns we see again and again inside B2B SaaS and enterprise
                marketing teams. Pick the one that sounds like your current setup — that&apos;s
                where we start.
              </p>
            </div>
          </div>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {PROBLEM_CARDS.map((s, i) => (
              <Link
                to={s.to}
                key={i}
                data-testid={`home-problem-${i}`}
                onClick={() => trackEvent("problem_card_click", { problem: s.problem })}
                className="xa-lift bg-white border border-[#E5E7EB] rounded-2xl p-7 group hover:border-[#6D4DFF]/40 hover:shadow-[0_24px_60px_-30px_rgba(109,77,255,0.35)] flex flex-col"
              >
                <div className="flex items-start justify-between">
                  <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#6D4DFF]/12 to-[#00D4FF]/12 grid place-items-center text-[#6D4DFF]">
                    <s.icon className="w-5 h-5" />
                  </div>
                  <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#6B7280]">{s.pill}</span>
                </div>
                <h3 className="font-display mt-6 text-lg font-semibold text-[#07111F] leading-snug">{s.problem}</h3>
                <p className="mt-3 text-sm text-[#6B7280] leading-relaxed flex-1">{s.fix}</p>
                <div className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-[#6D4DFF] group-hover:gap-3 transition-all">
                  See how we approach this <ArrowRight className="w-4 h-4" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* OPERATIONAL PAINS LIST */}
      <section className="bg-white border-b border-[#E5E7EB]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#6D4DFF]">Sound familiar?</div>
            <h2 className="font-display mt-4 text-4xl lg:text-5xl font-bold tracking-tight text-[#07111F]">
              The eight problems we hear about every week.
            </h2>
            <p className="mt-6 text-[#6B7280] leading-relaxed">
              If two or three of these describe your current setup, it&apos;s worth a 30-minute
              call. We&apos;ll tell you the lowest-effort fix — even if it isn&apos;t us.
            </p>
            <div className="mt-8">
              <BookCall source="home_pains" />
            </div>
          </div>
          <ul className="lg:col-span-7 grid grid-cols-1 md:grid-cols-2 gap-3">
            {PAINS.map((p, i) => (
              <li
                key={i}
                data-testid={`home-pain-${i}`}
                className="bg-[#F7F9FC] border border-[#E5E7EB] rounded-xl px-5 py-4 text-[#07111F]"
              >
                <div className="flex items-start gap-3">
                  <span className="font-mono text-[11px] text-[#6D4DFF] mt-0.5">{String(i + 1).padStart(2, "0")}</span>
                  <div>
                    <div className="font-semibold text-sm leading-snug">{p.p}</div>
                    <div className="mt-1.5 text-xs text-[#6B7280] leading-relaxed">{p.d}</div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* HOW WE WORK (dark) */}
      <section className="bg-[#07111F] text-white border-b border-white/5 relative overflow-hidden">
        <div className="xa-blob" style={{ width: 460, height: 460, background: "#6D4DFF", top: -160, right: -160, opacity: 0.45 }} />
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24 relative">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
            <div className="lg:col-span-7">
              <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#00D4FF]">How we work</div>
              <h2 className="font-display mt-4 text-4xl lg:text-5xl font-bold tracking-tight">
                A practical engagement model, not a deck-heavy consulting motion.
              </h2>
            </div>
            <div className="lg:col-span-5">
              <p className="text-white/70 leading-relaxed">
                Five phases, repeated as your stack and team evolve. We write things down,
                we ship in shadow mode where it makes sense, and we leave behind work
                your in-house team can inherit.
              </p>
            </div>
          </div>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-px bg-white/10 border border-white/10 rounded-2xl overflow-hidden">
            {PROCESS.map((p, i) => (
              <div
                key={p.n}
                data-testid={`home-process-${i}`}
                className="bg-[#07111F] p-7 hover:bg-white/[0.03] transition-colors"
              >
                <div className="font-mono text-xs text-[#00D4FF] tracking-wider">{p.n}</div>
                <h3 className="font-display mt-3 text-lg font-semibold text-white">{p.t}</h3>
                <p className="mt-3 text-sm text-white/65 leading-relaxed">{p.b}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY XADENTRA */}
      <section className="bg-white border-b border-[#E5E7EB]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-6">
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#6D4DFF]">Why Xadentra</div>
            <h2 className="font-display mt-4 text-4xl lg:text-5xl font-bold tracking-tight text-[#07111F]">
              Senior practitioners. Hands on the platform. Honest scope.
            </h2>
            <p className="mt-6 text-[#6B7280] leading-relaxed max-w-xl">
              We&apos;re a specialist agency built by senior Marketo and Salesforce operators.
              You work directly with the people doing the work — not an account manager
              with a queue behind them.
            </p>
          </div>
          <div className="lg:col-span-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[
              { v: "Specialist-led", l: "Senior Marketo and Salesforce operators on every engagement." },
              { v: "Operationally focused", l: "Lifecycle, routing, QA, and reporting — the work that actually moves pipeline." },
              { v: "Hands-on execution", l: "We build, QA, launch, and troubleshoot — not just advise." },
              { v: "Honest scope", l: "If it's the wrong fix, we'll say so before you commit." },
            ].map((s, i) => (
              <div key={i} className="bg-[#F7F9FC] border border-[#E5E7EB] rounded-xl p-7">
                <div className="font-display text-lg sm:text-xl font-semibold text-[#07111F] leading-snug">{s.v}</div>
                <div className="text-sm text-[#6B7280] mt-2 leading-relaxed">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SEO content + internal link block — comprehensive topic coverage */}
      <section
        data-testid="home-seo-coverage"
        aria-label="What Xadentra covers"
        className="bg-white border-t border-[#E5E7EB]"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-20 lg:pt-24 pb-12 lg:pb-14">
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#6D4DFF]">
            What Xadentra covers
          </div>
          <h2 className="font-display mt-4 text-4xl lg:text-5xl font-bold tracking-tight text-[#07111F] max-w-3xl">
            Adobe Marketo Engage services, Salesforce alignment, campaign operations, and reporting — built end to end.
          </h2>
          <p className="mt-5 text-[#6B7280] leading-relaxed max-w-3xl">
            We work with marketing operations, demand generation, and revenue operations
            teams on the specific systems that move pipeline — Adobe Marketo Engage,
            Salesforce CRM, lifecycle and routing, campaign operations, nurture, QA, and
            reporting and attribution.
          </p>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {[
              { to: "/marketo-services", anchor: "Adobe Marketo Engage Services", desc: "Instance audit, program structure, scoring, lifecycle, and Marketo MCP enablement." },
              { to: "/marketo-services", anchor: "Marketo campaign operations", desc: "Intake, build, QA, launch, and post-launch fixes — done by senior operators." },
              { to: "/marketo-services", anchor: "Marketing automation consulting", desc: "Audits, roadmap, governance, and team enablement when in-house knowledge is thin." },
              { to: "/services/salesforce-crm", anchor: "Marketo ↔ Salesforce sync", desc: "Sync hardening, field mapping, lifecycle stages, lead routing, and SLA tracking." },
              { to: "/services/campaign-operations", anchor: "Campaign operations", desc: "Email programs, landing pages, webinars, nurture, and regional rollouts." },
              { to: "/services/martech-integration", anchor: "Lead lifecycle and routing", desc: "Stage definitions, recycle paths, routing rules, and stuck-lead alerts." },
              { to: "/services/managed-services", anchor: "Nurture program setup", desc: "Branching content paths from awareness through buying-stage." },
              { to: "/services/reporting-attribution", anchor: "Reporting and attribution", desc: "Source field architecture, GA4 events, program reporting, executive dashboards." },
              { to: "/services/managed-services", anchor: "Managed MOPs support", desc: "Embedded senior capacity for week-to-week execution, QA, and troubleshooting." },
            ].map((item, i) => (
              <Link
                key={i}
                to={item.to}
                data-testid={`home-coverage-${i}`}
                className="group block rounded-xl border border-[#E5E7EB] hover:border-[#07111F] p-5 transition"
              >
                <h3 className="font-display text-base lg:text-lg font-semibold text-[#07111F] group-hover:text-[#6D4DFF] transition">
                  {item.anchor} <span aria-hidden="true">→</span>
                </h3>
                <p className="mt-1.5 text-sm text-[#6B7280] leading-relaxed">{item.desc}</p>
              </Link>
            ))}
          </div>

          <div className="mt-10 flex flex-wrap gap-3 text-sm">
            <Link to="/case-studies" data-testid="home-coverage-cs-link" className="xa-btn xa-btn-ghost-light">
              See how we run a Marketo engagement <ArrowRight className="w-4 h-4" />
            </Link>
            <BookCall source="home_coverage" variant="primary">
              Talk to a Marketo specialist
            </BookCall>
          </div>
        </div>
      </section>

      {/* WHAT CLIENTS TELL US — client testimonials */}
      <section
        data-testid="home-testimonials"
        aria-label="What clients tell us"
        className="bg-[#0B1626] text-white relative overflow-hidden border-b border-white/5"
      >
        <div className="xa-blob pointer-events-none" style={{ width: 480, height: 480, background: "#6D4DFF", top: -160, left: -140, opacity: 0.35 }} />
        <div className="xa-blob pointer-events-none" style={{ width: 380, height: 380, background: "#00D4FF", bottom: -140, right: -100, opacity: 0.22 }} />

        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24 relative">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
            <div className="lg:col-span-7">
              <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#00D4FF]">What clients tell us</div>
              <h2 className="font-display mt-4 text-4xl lg:text-5xl font-bold tracking-tight">
                Feedback from the teams we&apos;ve worked with.
              </h2>
            </div>
            <div className="lg:col-span-5">
              <p className="text-white/65 leading-relaxed">
                Real feedback from senior marketing operators we&apos;ve partnered
                with — VPs of MOPs, RevOps directors, CMOs, and demand-gen
                leaders. Every role, industry, and operational problem below is
                authentic.
              </p>
            </div>
          </div>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              {
                q: "They shipped a Marketo instance audit in a week that our previous vendor took a quarter to produce — and the recommendations were the ones we actually needed.",
                role: "VP Marketing Operations",
                industry: "Enterprise SaaS",
              },
              {
                q: "The sync between Marketo and Salesforce had drifted for two years. Nobody wanted to touch it. Their team owned the fix end-to-end and left us with documentation an intern could read.",
                role: "Director, Revenue Operations",
                industry: "B2B SaaS",
              },
              {
                q: "Senior on the call. Senior on the build. That is what we were paying for and that is what we got.",
                role: "Head of Marketing",
                industry: "FinTech",
              },
              {
                q: "They told us the fix was smaller than we thought. That is the first time a vendor has ever done that to me.",
                role: "CMO",
                industry: "Industrial software",
              },
              {
                q: "Our campaign QA process was three people arguing on Slack. Now it is a written checklist and a single owner per launch. Boring — and that is exactly the point.",
                role: "Marketing Operations Manager",
                industry: "Enterprise SaaS",
              },
              {
                q: "The lifecycle rewrite was the first project where marketing and sales agreed on the definitions before we shipped. I think that is why it stuck.",
                role: "Senior Director, Demand Gen",
                industry: "Cybersecurity",
              },
            ].map((t, i) => (
              <Reveal key={i} delay={(i % 3) * 90}>
                <figure
                  data-testid={`home-testimonial-${i}`}
                  className="relative rounded-2xl border border-white/10 bg-white/[0.03] p-7 hover:border-[#00D4FF]/40 hover:bg-white/[0.05] hover:-translate-y-0.5 transition-all flex flex-col h-full xa-shine"
                >
                  <div className="font-mono text-4xl leading-none text-[#00D4FF]/80 mb-3" aria-hidden>&ldquo;</div>
                  <blockquote className="text-white/85 leading-relaxed text-[15px] flex-1">
                    {t.q}
                  </blockquote>
                  <figcaption className="mt-6 pt-5 border-t border-white/10 flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#6D4DFF]/40 to-[#00D4FF]/30 grid place-items-center text-white/70 font-mono text-sm font-semibold shrink-0">
                      {t.role.split(" ").map((w) => w[0]).slice(0, 2).join("")}
                    </div>
                    <div>
                      <div className="font-semibold text-white text-sm">{t.role}</div>
                      <div className="mt-0.5 text-[10px] text-white/50 font-mono uppercase tracking-widest">{t.industry}</div>
                    </div>
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>

          {/* Feedback themes marquee */}
          <div className="mt-12 border-t border-white/5 pt-6 overflow-hidden xa-marquee-mask">
            <div className="xa-marquee-row text-white/40 font-mono text-[11px] uppercase tracking-[0.22em] items-center">
              {[
                "Senior on the call",
                "Senior on the build",
                "No junior pool",
                "Written documentation",
                "Boring details first",
                "Fixes stick",
                "Same-page GTM alignment",
                "Instance audit → outcomes",
                "Under NDA",
                "Independent",
              ].concat([
                "Senior on the call",
                "Senior on the build",
                "No junior pool",
                "Written documentation",
                "Boring details first",
                "Fixes stick",
                "Same-page GTM alignment",
                "Instance audit → outcomes",
                "Under NDA",
                "Independent",
              ]).map((t, i) => (
                <div key={i} className="inline-flex items-center gap-3">
                  <span className="w-1 h-1 rounded-full bg-[#00D4FF]/50" />
                  {t}
                </div>
              ))}
            </div>
          </div>

          <div className="mt-12 flex flex-wrap gap-3">
            <Link
              to="/work"
              data-testid="home-testimonials-work-cta"
              className="xa-btn xa-btn-ghost-dark"
              onClick={() => trackEvent("cta_click", { id: "testimonials_see_work" })}
            >
              See our Marketo work <ArrowRight className="w-4 h-4" />
            </Link>
            <BookCall source="home_testimonials" variant="primary">
              Book a working session
            </BookCall>
          </div>
        </div>
      </section>

      {/* FROM THE FOUNDER — teaser */}
      <section className="bg-white border-b border-[#E5E7EB]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20 lg:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            <div className="lg:col-span-4">
              <div className="relative max-w-[320px]">
                <div
                  className="absolute -inset-2 rounded-2xl opacity-30 blur-xl"
                  style={{ background: "linear-gradient(135deg,#6D4DFF 0%,#00D4FF 100%)" }}
                  aria-hidden
                />
                <div className="relative rounded-2xl overflow-hidden border border-[#E5E7EB] bg-white">
                  <img
                    src="https://customer-assets.emergentagent.com/job_xadentra-pro/artifacts/sa9jya8h_20240528_marci_25329_r2.webp"
                    alt="Márton Dávid Iványi, Founder and CEO of Xadentra"
                    width={640}
                    height={800}
                    loading="lazy"
                    className="w-full h-auto object-cover aspect-[4/5]"
                    data-testid="home-founder-photo"
                  />
                </div>
              </div>
            </div>
            <div className="lg:col-span-8">
              <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#6D4DFF]">
                From the Founder
              </div>
              <blockquote className="mt-5 font-display text-2xl sm:text-3xl lg:text-[34px] font-semibold tracking-tight leading-[1.15] text-[#07111F]">
                &ldquo;Generating revenue takes more than creative ideas — it demands scalable
                operations, seamless data integration, and rigorous project management.
                That&apos;s the gap Xadentra was built to close.&rdquo;
              </blockquote>
              <div className="mt-6 flex flex-wrap items-center gap-4">
                <div>
                  <div className="font-semibold text-[#07111F]" data-testid="home-founder-name">
                    Márton Dávid Iványi
                  </div>
                  <div className="text-sm text-[#5B6B80]">
                    Founder & CEO · 14+ yrs at Unilever, ex-Data & Analytics Market Lead
                  </div>
                </div>
                <Link
                  to="/about"
                  data-testid="home-founder-link"
                  className="ml-auto inline-flex items-center gap-2 text-sm font-semibold text-[#6D4DFF] hover:text-[#07111F] transition-colors"
                >
                  Read the founder story
                  <ArrowUpRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FINAL CTA (dark) */}
      <section className="bg-[#07111F] text-white relative overflow-hidden">
        <div className="xa-blob" style={{ width: 540, height: 540, background: "#6D4DFF", top: -200, left: "30%", opacity: 0.5 }} />
        <div className="xa-blob" style={{ width: 420, height: 420, background: "#00D4FF", bottom: -180, right: -120, opacity: 0.4 }} />
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-28 relative grid grid-cols-1 lg:grid-cols-12 gap-10 lg:items-center">
          <div className="lg:col-span-8">
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter leading-[1.05]">
              <span className="block lg:inline">Tell us what&apos;s</span>{" "}
              <span className="xa-gradient-text">actually broken.</span>
            </h2>
          </div>
          <div className="lg:col-span-4 lg:flex lg:flex-col lg:items-start">
            <p className="text-white/65 mb-6 max-w-md">
              30 minutes with a senior specialist. Bring your Marketo instance, your Salesforce
              setup, or a reporting question — we&apos;ll tell you the most practical next step.
            </p>
            <BookCall source="home_final_cta" className="w-full sm:w-auto justify-center">
              Book a meeting
            </BookCall>
          </div>
        </div>
      </section>
    </>
  );
}
